import type { LeadFormProps, LeadFormStepFields } from "@component/assets/AbstractLeadForm/Steps/BasicLeadForm";
import { BasicLeadForm } from "@component/assets/AbstractLeadForm/Steps/BasicLeadForm";
import { formatText } from "@ui/format";
import autobind from "autobind-decorator";
import { FormCity, FormCurrency, List, __ } from "bm-ui-kit";
import type { ReactNode } from "react";
import { Card } from "react-bootstrap";
import { CONFIG } from "../../Calculator/data";
import type { MortgageLeadFormVariant, ProductRequisitionTime } from "../types";

export interface MortgageStepFields extends LeadFormStepFields {
	loanAmount?: number;
	productRequisitionTime: ProductRequisitionTime;
}

export interface MortgageLeadFormStepProps extends LeadFormProps {
	withLoanAmount: boolean;
	variant: MortgageLeadFormVariant;
	useNewDesign?: boolean;
}

export class LeadForm<TProps extends MortgageLeadFormStepProps = MortgageLeadFormStepProps> extends BasicLeadForm<
	MortgageStepFields,
	TProps
> {
	protected renderFields(): ReactNode {
		const { withLoanAmount, submitState } = this.props;

		return (
			<>
				{this.renderExtraHeader()}
				{super.renderFields()}
				{withLoanAmount && (
					<FormCurrency
						name="loanAmount"
						placeholder={__("Mennyi hitelt szeretnél?")}
						validate={this.validateLoanAmount}
						disabled={submitState !== undefined}
					/>
				)}
			</>
		);
	}

	protected renderHeader(): ReactNode {
		const { title, useNewDesign } = this.props;

		if (useNewDesign) {
			return (
				<Card.Header className="header-primary py-2 h4">
					<div className="mb-0 text-center">{title}</div>
				</Card.Header>
			);
		}

		return super.renderHeader();
	}

	private renderExtraHeader(): ReactNode {
		const { variant } = this.props;

		if (variant === "consumerFriendly") {
			return (
				<List itemStyle="tick" className="mb-3 mt-2 customer-friendly-header">
					<List.Item>Támogatott hitelt, vagy piaci hitelt válasszak?</List.Item>
					<List.Item>
						Drágább, de biztonságos, vagy olcsóbb, de változó kamatozású hitelt válasszak?
					</List.Item>
					<List.Item>Mennyibe kerül, ha a futamidő előtt vissza szeretném fizetni a hitelt?</List.Item>
				</List>
			);
		}

		return null;
	}

	protected renderCityField(): ReactNode {
		const { submitState } = this.props;

		return (
			<FormCity
				placeholder="Hol intéznéd a lakáshitelt?"
				name="cityName"
				disabled={submitState !== undefined}
				required
			/>
		);
	}

	@autobind
	private validateLoanAmount(loanAmount: number): string {
		if (isNaN(loanAmount)) {
			return "A hitelösszeg megadása kötelező!";
		}

		if (loanAmount < CONFIG.MIN_LOAN_AMOUNT) {
			return `A minimálisan felvehető hitelösszeg ${formatText(CONFIG.MIN_LOAN_AMOUNT, "ft")}!`;
		}

		if (loanAmount > CONFIG.MAX_LOAN_AMOUNT) {
			return `A maximálisan felvehető hitelösszeg ${formatText(CONFIG.MAX_LOAN_AMOUNT, "ft")}!`;
		}
	}

	protected getComponentName(): string {
		return `${super.getComponentName()}.LeadForm`;
	}
}
